import { QueryLazyOptions, useApolloClient } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import {
  FinleyAi,
  FinleyAi as FinleyAiTypes,
} from '@finley-technologies/finley-types';

import { Exact } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  GET_FINLEY_AI_THREADS,
  GET_KNOWLEDGE_BASE_DOCUMENTS_BY_ORGANIZATION_ID,
} from '../requests/ai.gql';
import {
  useGetFinleyAiThreadWithPromptResponsesLazyQueryGQLType,
  useProduceFinleyAiWorkleaseMutation,
} from '../requests/ai.gql.generated';
import {
  deserializeFinleyAiThread,
  deserializeFinleyAiThreadWithPromptResponses,
} from '../ui/FinleyAiThreads';
import {
  convertToFinleyAiRequest,
  generateFinleyAiViewContextFromThread,
  initFinleyAiViewContext,
  returnIsTerminalStatus,
  returnIsThreadFailed,
} from '../ui/FinleyAiUtils';
import { FinleyAiViewContext } from '../ui/types';
import { errorToastConfig } from 'packages/common-frontend/constants';

export interface UseFinleyAiThreadsInputs {
  prompt: string;
  setPrompt: React.Dispatch<React.SetStateAction<string>>;
  tempPrompt: string;
  setTempPrompt: React.Dispatch<React.SetStateAction<string>>;
  initialViewContext: FinleyAiViewContext;
  promptTemplate?: string;
}
export type UseFinleyAiThreadsOutput = {
  onReset: (params: {
    finleyAiType: FinleyAi.FinleyAiType;
    targetOrgId: string;
  }) => void;
  selectedThread: FinleyAiTypes.FinleyAiThreadWithPromptResponses | null;
  setSelectedThread: React.Dispatch<
    React.SetStateAction<FinleyAiTypes.FinleyAiThreadWithPromptResponses | null>
  >;
  isProducingMessage: boolean;
  isThreadLoading: boolean;
  setIsThreadLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isThreadFailed: boolean;
  viewContext: FinleyAiViewContext;
  setViewContext: React.Dispatch<React.SetStateAction<FinleyAiViewContext>>;
  onSendPrompt: (promptToSend?: string) => void;
  handleAthenaSqlExplain: (sql: string) => void;
  getFinleyAiThreadWithPromptResponses: (
    options?:
      | QueryLazyOptions<
          Exact<{
            finleyAiThreadId: string;
          }>
        >
      | undefined,
  ) => void;
  handleFinleyAiThreadSelection: (thread: FinleyAiTypes.FinleyAiThread) => void;
  handleDocumentAmaUpload: ({
    documentName,
    s3Bucket,
    s3Path,
  }: {
    documentName: string;
    s3Bucket: string;
    s3Path: string;
  }) => void;
};

export const useFinleyAiThreads = ({
  prompt,
  setPrompt,
  tempPrompt,
  setTempPrompt,
  initialViewContext,
  promptTemplate,
}: UseFinleyAiThreadsInputs): UseFinleyAiThreadsOutput => {
  const client = useApolloClient();
  const toast = useToast();

  const [viewContext, setViewContext] = useState<FinleyAiViewContext>(
    initialViewContext,
  );
  const [
    selectedThread,
    setSelectedThread,
  ] = useState<FinleyAiTypes.FinleyAiThreadWithPromptResponses | null>(null);

  const [isThreadLoading, setIsThreadLoading] = useState<boolean>(false);

  const { finleyAiThreadId, targetOrganizationId } = viewContext;

  const [
    getFinleyAiThreadWithPromptResponses,
    {
      stopPolling: stopGetFinleyAiThreadPromptResponsesPolling,
      called: isGetFinleyAiThreadWithPromptResponsesCalled,
    },
  ] = useGetFinleyAiThreadWithPromptResponsesLazyQueryGQLType({
    fetchPolicy: 'network-only',
    pollInterval: 5000,
    onError: () => {
      toast({
        ...errorToastConfig,
        title: `An error occurred while attempting to load Finley Ai Thread with responses.`,
      });

      // stop polling after an error.
      stopGetFinleyAiThreadPromptResponsesPolling();
    },
    onCompleted: async (data) => {
      if (data.getFinleyAiThreadWithPromptResponses) {
        const threadWithPromptResponses = deserializeFinleyAiThreadWithPromptResponses(
          data.getFinleyAiThreadWithPromptResponses.threadWithPromptResponses,
        );

        if (returnIsTerminalStatus(threadWithPromptResponses)) {
          if (stopGetFinleyAiThreadPromptResponsesPolling) {
            stopGetFinleyAiThreadPromptResponsesPolling();
            const lastPr =
              threadWithPromptResponses.promptResponses[
                threadWithPromptResponses.promptResponses.length - 1
              ];

            if (lastPr && lastPr.rawPrompt.subType === 'upload') {
              await client.refetchQueries({
                include: [GET_KNOWLEDGE_BASE_DOCUMENTS_BY_ORGANIZATION_ID],
                onQueryUpdated: (observableQuery) => {
                  return observableQuery.refetch({ targetOrganizationId });
                },
              });
            }
          }

          setIsThreadLoading(false);
        }

        setSelectedThread(threadWithPromptResponses);

        const newViewContextFromThread = generateFinleyAiViewContextFromThread({
          thread: threadWithPromptResponses,
        });

        setViewContext(newViewContextFromThread);
      }
    },
    notifyOnNetworkStatusChange: true,
  });

  const [
    produceFinleyAiWorkleaseMutation,
    { loading: isProducingMessage },
  ] = useProduceFinleyAiWorkleaseMutation({
    onCompleted: (data) => {
      const { thread } = data.produceFinleyAiWorklease;

      const finleyAiThread = deserializeFinleyAiThread(thread);

      // start polling
      getFinleyAiThreadWithPromptResponses({
        variables: { finleyAiThreadId: finleyAiThread.id },
      });
    },
    refetchQueries: [GET_FINLEY_AI_THREADS],
    onError: (err) => {
      toast({
        ...errorToastConfig,
        title: `An error occurred while attempting to utilize FinleyAi. Error: ${err.message}`,
      });

      setPrompt(tempPrompt);
      setIsThreadLoading(false);
    },
  });

  // Callbacks
  const onReset = useCallback(
    ({
      finleyAiType,
      targetOrgId,
    }: {
      finleyAiType: FinleyAi.FinleyAiType;
      targetOrgId: string;
    }) => {
      setViewContext(
        initFinleyAiViewContext({
          type: finleyAiType,
          targetOrganizationId: targetOrgId,
          promptTemplate,
        }),
      );

      if (
        stopGetFinleyAiThreadPromptResponsesPolling &&
        isGetFinleyAiThreadWithPromptResponsesCalled
      ) {
        stopGetFinleyAiThreadPromptResponsesPolling();
      }

      // reset prompt
      setPrompt('');

      // clear selected thread
      setSelectedThread(null);
      setIsThreadLoading(false);
    },
    [
      setViewContext,
      stopGetFinleyAiThreadPromptResponsesPolling,
      isGetFinleyAiThreadWithPromptResponsesCalled,
      setPrompt,
      setSelectedThread,
      setIsThreadLoading,
      promptTemplate,
    ],
  );

  const handleAthenaSqlExplain = useCallback(
    (sql: string): void => {
      if (finleyAiThreadId) {
        setIsThreadLoading(true);

        const finleyAiRequest: FinleyAiTypes.SqlExplainRequest = {
          finleyAiThreadId,
          type: 'athenaSqlGeneration',
          subType: 'sqlExplain',
          sql,
          targetOrganizationId,
        };

        produceFinleyAiWorkleaseMutation({
          variables: {
            params: { request: finleyAiRequest },
          },
        });

        // store the prompt in case producing the message fails
        // so we can restore it
        setTempPrompt(prompt);
      } else {
        toast({
          ...errorToastConfig,
          title: 'A Finley AI thread must be selected to explain a SQL query.',
        });
      }
    },
    [
      finleyAiThreadId,
      produceFinleyAiWorkleaseMutation,
      prompt,
      targetOrganizationId,
      toast,
      setTempPrompt,
    ],
  );

  const handleDocumentAmaUpload = useCallback(
    ({
      documentName,
      s3Bucket,
      s3Path,
    }: {
      documentName: string;
      s3Bucket: string;
      s3Path: string;
    }): void => {
      if (finleyAiThreadId === null) {
        setIsThreadLoading(true);

        const finleyAiRequest: FinleyAiTypes.DocumentAmaUploadRequest = {
          finleyAiThreadId,
          type: 'documentAma',
          subType: 'upload',
          targetOrganizationId,
          documentName,
          s3Bucket,
          s3Path,
          documentType: 'userProvided',
          tags: [], // tags are not support yet
        };

        produceFinleyAiWorkleaseMutation({
          variables: {
            params: { request: finleyAiRequest },
          },
        });

        // store the prompt in case producing the message fails
        // so we can restore it
        setTempPrompt(prompt);
      } else {
        toast({
          ...errorToastConfig,
          title: 'Please start a new thread to upload a document.',
        });
      }
    },
    [
      finleyAiThreadId,
      produceFinleyAiWorkleaseMutation,
      prompt,
      targetOrganizationId,
      toast,
      setTempPrompt,
    ],
  );

  const onSendPrompt = useCallback(
    (promptToSend?: string) => {
      setIsThreadLoading(true);

      const finleyAiRequest = convertToFinleyAiRequest({
        viewContext,
        prompt: promptToSend ?? prompt,
      });

      // store the prompt in case producing the message fails
      // so we can restore it
      setTempPrompt(promptToSend ?? prompt);

      setPrompt('');

      produceFinleyAiWorkleaseMutation({
        variables: {
          params: { request: finleyAiRequest },
        },
      });
    },
    [
      setIsThreadLoading,
      setTempPrompt,
      prompt,
      setPrompt,
      produceFinleyAiWorkleaseMutation,
      viewContext,
    ],
  );

  const handleFinleyAiThreadSelection = useCallback(
    (thread: FinleyAiTypes.FinleyAiThread) => {
      // reset selected thread
      setSelectedThread(null);

      // clear prompt
      setPrompt('');

      // init loading
      setIsThreadLoading(true);

      // start polling
      getFinleyAiThreadWithPromptResponses({
        variables: { finleyAiThreadId: thread.id },
      });
    },
    [
      getFinleyAiThreadWithPromptResponses,
      setIsThreadLoading,
      setSelectedThread,
      setPrompt,
    ],
  );

  const isThreadFailed = useMemo(() => {
    if (!selectedThread) {
      return false;
    }

    return returnIsThreadFailed(selectedThread);
  }, [selectedThread]);

  return {
    onReset,
    selectedThread,
    setSelectedThread,
    isThreadLoading,
    setIsThreadLoading,
    viewContext,
    setViewContext,
    isThreadFailed,
    onSendPrompt,
    handleAthenaSqlExplain,
    handleDocumentAmaUpload,
    handleFinleyAiThreadSelection,
    isProducingMessage,
    getFinleyAiThreadWithPromptResponses,
  };
};
